import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  /* `hover-elevate` and `active-elevate-2` are how the old application answers
     the pointer: a wash laid over the button rather than its own fill fading,
     so a ghost button on a card and a filled one beside it darken by the same
     amount. */
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-[color,background-color,border-color,box-shadow,opacity,transform] duration-150 ease-out outline-hidden hover-elevate active-elevate-2 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/80 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        /* A filled button carries an edge computed from its own fill, which is
           what keeps a primary button from reading as a flat rectangle of
           colour on a white card. The key light and the press are what make
           it read as a thing rather than a region: a hairline of light on its
           top edge, and 3% smaller under the finger. Ghost and link stay
           still, as text does. */
        default:
          "border border-primary-border bg-primary text-primary-foreground shadow-key motion-safe:active:scale-[0.97]",
        destructive:
          "border border-destructive-border bg-destructive text-white shadow-key focus-visible:ring-destructive/20 motion-safe:active:scale-[0.97] dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        outline:
          "border border-(--button-outline) bg-card shadow-key-soft motion-safe:active:scale-[0.97] dark:bg-input/30",
        secondary:
          "border border-secondary-border bg-secondary text-secondary-foreground shadow-key-soft motion-safe:active:scale-[0.97]",
        /* A transparent border, so turning one on later does not move anything. */
        ghost: "border border-transparent",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        /* Minimum heights, not fixed ones, so a button with an unusual amount
           of content grows instead of clipping — and the old application's own
           numbers: 32 for a control, 28 for the small one, 40 for a marketing
           call to action. */
        default: "min-h-8 px-4 py-1 has-[>svg]:px-3",
        xs: "min-h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "min-h-7 gap-1.5 rounded-md px-3 text-xs has-[>svg]:px-2.5",
        lg: "min-h-10 rounded-md px-8 has-[>svg]:px-6",
        icon: "size-8",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-7",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * Whether something is rendering inside a button. The spinner reads it: a
 * busy button already says what it is doing in its label ("Saving…"), so a
 * spinner in it is decoration, where one standing alone is the only thing
 * telling a screen reader the panel is still loading.
 */
const InsideButton = React.createContext(false);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <InsideButton.Provider value>
      <Comp
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    </InsideButton.Provider>
  );
}

export { Button, buttonVariants, InsideButton };
